'use client';

import { useTranslations } from 'next-intl';
import type { Table } from '@/lib/table-features';
import { Loader2, Trash2 } from 'lucide-react';

import { useHashDialog } from '@/hooks/use-hash-dialog';
import { cn } from '@/lib/utils';
import type { BulkPayload } from '@/types/bulk-action';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { CLIENTS_HASH } from '../constants';
import { useBulkDeleteClientsMutation } from '../services/clients.mutations';
import type { Client } from '../types';

interface ClientsTableActionBarProps {
  table: Table<Client>;
  getBulkPayload: () => BulkPayload;
  onComplete: () => void;
  isAllPagesSelected?: boolean;
  totalCount?: number;
}

export function ClientsTableActionBar({
  table,
  getBulkPayload,
  onComplete,
  isAllPagesSelected,
  totalCount
}: ClientsTableActionBarProps) {
  const t = useTranslations('clients');
  const bulkDelete = useHashDialog(CLIENTS_HASH.bulkDelete);

  const bulkDeleteMutation = useBulkDeleteClientsMutation();

  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const displayCount =
    isAllPagesSelected && totalCount ? totalCount : selectedRows.length;

  async function handleBulkDelete() {
    const payload = getBulkPayload();
    try {
      if (payload.mode === 'all') {
        await bulkDeleteMutation.mutateAsync({
          all: true,
          filters: payload.filters
        });
      } else {
        await bulkDeleteMutation.mutateAsync({ ids: payload.ids });
      }
      onComplete();
    } catch {
      // handled by global MutationCache
    }
    bulkDelete.onOpenChange(false);
  }

  return (
    <>
      <div className='bg-muted flex items-center gap-3 rounded-lg px-4 py-2'>
        <span className='text-muted-foreground text-sm font-medium'>
          {t('selectedCount', { count: displayCount })}
        </span>
        <div className='flex items-center gap-2'>
          <Button
            variant='destructive'
            size='sm'
            onClick={() => bulkDelete.onOpenChange(true)}
          >
            <Trash2 className='me-1.5 h-3.5 w-3.5' />
            {t('bulkDelete')}
          </Button>
        </div>
      </div>

      <AlertDialog
        open={bulkDelete.open}
        onOpenChange={bulkDelete.onOpenChange}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('bulkDeleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('bulkDeleteConfirmDescription', { count: displayCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className={cn(
                'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              )}
            >
              {bulkDeleteMutation.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
